import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Box, Button, CircularProgress, Dialog, Typography } from "@mui/material";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import logo from "../../assets/images/jobHarvestTransparent.png";
import { MAINTENANCE_COPY, maintenance as defaultMonitor } from "../../utils/maintenance";

/**
 * The "we're down" screen, over every page (signed in or out) while the
 * maintenance switch in AWS is on. See utils/maintenance.js for when it checks.
 *
 * A full-screen MUI Dialog: it traps focus, marks the rest of the page inert for
 * screen readers, and ignores Escape and backdrop clicks, because there is
 * nothing usable behind it. "Try again" checks right away instead of making the
 * user wait for the next scheduled check.
 */
export default function MaintenanceOverlay({ monitor = defaultMonitor }) {
  const theme = useTheme();
  const { on, message } = useSyncExternalStore(monitor.subscribe, monitor.getSnapshot);
  // Only the user's own retry shows a spinner; the scheduled checks run silently behind the screen.
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    monitor.start();
    return () => monitor.stop();
  }, [monitor]);

  if (!on) return null;

  return (
    <Dialog
      open
      fullScreen
      disableEscapeKeyDown
      onClose={() => {}}
      aria-labelledby="maintenance-title"
      aria-describedby="maintenance-body"
      // Above every layer the app draws: the cookie banner (2000), the home page's grain
      // and mobile menu (10000) and the dashboard's popovers (99999).
      sx={{ zIndex: 100000 }}
      PaperProps={{
        role: "alertdialog",
        "data-testid": "maintenance-overlay",
        sx: { background: "var(--fa-page-bg)" },
      }}
    >
      <Box
        sx={{
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            textAlign: "center",
            backgroundColor: theme.palette.common.white,
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(44, 62, 80, 0.12)",
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
          }}
        >
          <Box component="img" src={logo} alt="JobHarvest" sx={{ height: 36, mb: 3 }} />
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha(theme.palette.custom.main, 0.14),
            }}
          >
            <CloudOffOutlinedIcon sx={{ fontSize: 32, color: theme.palette.custom.textOnTint }} />
          </Box>
          <Typography
            id="maintenance-title"
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, color: theme.palette.custom.mainTitle, mb: 1.5 }}
          >
            {MAINTENANCE_COPY.title}
          </Typography>
          <Typography
            id="maintenance-body"
            variant="body1"
            sx={{ color: "text.secondary", mb: 3, whiteSpace: "pre-line" }}
          >
            {message || MAINTENANCE_COPY.body}
          </Typography>
          <Button
            variant="contained"
            autoFocus
            disabled={retrying}
            onClick={async () => {
              setRetrying(true);
              try {
                await monitor.check();
              } finally {
                setRetrying(false);
              }
            }}
            startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              color: theme.palette.common.white,
              backgroundColor: theme.palette.custom.main,
              "&:hover": { backgroundColor: theme.palette.custom.mainSelected },
              minWidth: 160,
            }}
          >
            {MAINTENANCE_COPY.retry}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
